import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  COLOR_LABEL,
  COLOR_LABEL_KEYS,
  COLOR_SCALE_MAP,
  COLORS
} from "@/constants/colors";
import { DEFAULT_STEP } from "@/constants/dataset";
import { ONLY_NUMERIC_REGEX } from "@/constants/regex";
import { ColorMap } from "@/types/dataset";

import { twMerge } from "tailwind-merge";

type ShadeKeys = ReadonlyArray<string | number>;

interface ColorScaleSelectProps {
  titleClassName?: string;
  selectContentClassName?: string;
  selectTriggerClassName?: string;
  value?: ColorMap;
  onValueChange?: (value: ColorMap) => void;
  type?: "fill" | "stroke";
  stepValue?: number;
  onStepValueChange?: (value: number) => void;
  isNewUIDatasetConfigEnabled?: boolean;
}

const ColorScaleSelect = ({
  titleClassName,
  selectTriggerClassName,
  selectContentClassName,
  value,
  onValueChange,
  stepValue = DEFAULT_STEP,
  onStepValueChange,
  type = "fill",
  isNewUIDatasetConfigEnabled = false
}: ColorScaleSelectProps) => {
  // Default color list & shade keys
  const isFillOrStroke = type === "fill" || type === "stroke";
  const list = isFillOrStroke ? COLORS : COLOR_LABEL;

  const baseShade =
    Number(stepValue) >= 2 && Number(stepValue) <= 10
      ? COLOR_SCALE_MAP[stepValue]
      : COLOR_SCALE_MAP["10"];
  const shades: ShadeKeys = isFillOrStroke ? baseShade : COLOR_LABEL_KEYS;

  return (
    <div className="w-full space-y-2">
      <p className={twMerge("text-grey text-xs mb-1", titleClassName)}>
        Color Scale
      </p>

      <div className="flex gap-3">
        <Select
          value={value}
          onValueChange={(val) => {
            onValueChange?.(val as ColorMap);
          }}
        >
          <SelectTrigger
            data-testid="color-scale-select"
            className={twMerge(
              "rounded-xl h-auto w-full [&>span]:w-full",
              selectTriggerClassName
            )}
          >
            <SelectValue
              placeholder="Select a color scale"
              className="w-full"
            />
          </SelectTrigger>

          <SelectContent className={selectContentClassName}>
            <SelectGroup className="w-full">
              {Object.entries(list).map(([colorName, colorScale]) => (
                <SelectItem
                  key={colorName}
                  value={colorName}
                  data-testid={`color-scale-option-${colorName}`}
                  className="flex items-center gap-2 py-2 px-3 w-full [&>span]:w-full"
                >
                  <div className="flex h-3 w-full rounded-sm overflow-hidden border">
                    {shades.map((shade) => (
                      <div
                        key={String(shade)}
                        className="flex-1 h-full"
                        style={{
                          backgroundColor: colorScale[shade]
                        }}
                      />
                    ))}
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        {isNewUIDatasetConfigEnabled && isFillOrStroke && (
          <input
            data-testid="color-scale-step-input"
            value={stepValue}
            min={2}
            max={10}
            type="number"
            onChange={(e) => {
              const value = e.target.value.replace(ONLY_NUMERIC_REGEX, "");
              if (Number(value) > 10) {
                onStepValueChange?.(10);
                return;
              }
              onStepValueChange?.(Number(value));
            }}
            onBlur={(e) => {
              if (Number(e.target.value) > 10 || Number(e.target.value) < 2) {
                onStepValueChange?.(10);
              }
            }}
            className="w-10 flex-grow-0 flex-shrink-1 text-center border rounded-xl p-2 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
        )}
      </div>
    </div>
  );
};

export default ColorScaleSelect;
